import { type Candidate } from "@vidario/domain";
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { useVidarioClient } from "../client";
import { type CandidatePatch } from "../endpoints";
import { queryKeys } from "../queryKeys";

export interface UpdateCandidateInput {
  /** Required for cache invalidation. The candidate response doesn't
   * carry a usable `jobId` on the Django backend (the FK is serialized
   * as `job`, which `snakeToCamel` doesn't rewrite), so callers pass
   * the jobId from URL params or the parent route. */
  jobId: string;
  candidateId: string;
  patch: CandidatePatch;
}

/** Partial candidate update. Drives the Candidate Detail screen's notes
 * (recruiterNotes / internalNotes / interviewNotes), the interview
 * key-findings bullet list, skill-match override toggles, profile edits
 * (contact data, attachments, basics for admins), and the Empfehlung
 * screen's inline recommendation-text + rank edits. */
export function useUpdateCandidate(): UseMutationResult<Candidate, Error, UpdateCandidateInput> {
  const client = useVidarioClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, patch }) => client.updateCandidate({ candidateId, patch }),
    onSuccess: (candidate, { jobId, candidateId }) => {
      qc.setQueryData(queryKeys.candidate(jobId, candidateId), candidate);
      qc.invalidateQueries({ queryKey: ["jobs", jobId, "candidates"] });
      // Skill-match overrides, note edits and profile changes append an
      // entry to candidate_log server-side, so refetch the log too — key
      // shape matches useCandidateLog: ["candidates", id, "log"].
      qc.invalidateQueries({ queryKey: ["candidates", candidateId, "log"] });
    },
  });
}
